import { createIcons, icons as coreIcons } from 'lucide';
import type { CreateIconsOptions, IconNode, Icons } from 'lucide';

export type LucideIconName = keyof typeof coreIcons;
export type LucideIconRegistry = Record<string, IconNode>;

type InitOptions = Partial<Omit<CreateIconsOptions, 'icons'>>;

type IconAttributes = NonNullable<CreateIconsOptions['attrs']>;

const customIcons: Icons = {};

let defaultAttributes: IconAttributes = {
    width: '1em',
    height: '1em',
    'stroke-width': 1.5,
    'aria-hidden': 'true'
};

const defaultOptions: InitOptions = {
    nameAttr: 'data-lucide',
    inTemplates: true
};

function resolveRoot(root?: Element | Document | DocumentFragment): Element | Document | DocumentFragment | undefined {
    if (root) {
        return root;
    }

    if (typeof document !== 'undefined') {
        return document;
    }

    return undefined;
}

function buildIconMap(): Icons {
    return {
        ...coreIcons,
        ...customIcons
    };
}

export function registerLucideIcons(iconMap: LucideIconRegistry): void {
    Object.assign(customIcons, iconMap);
}

export function setLucideDefaultAttributes(attrs: IconAttributes): void {
    defaultAttributes = {
        ...defaultAttributes,
        ...attrs
    };
}

export function initLucideIcons(options: InitOptions = {}): void {
    const { attrs, root, ...rest } = options;
    const targetRoot = resolveRoot(root);

    if (!targetRoot) {
        return;
    }

    const mergedAttrs: IconAttributes = {
        ...defaultAttributes
    };

    if (attrs) {
        Object.assign(mergedAttrs, attrs);
    }

    createIcons({
        ...defaultOptions,
        ...rest,
        root: targetRoot,
        attrs: mergedAttrs,
        icons: buildIconMap()
    });
}

export function refreshLucideIcons(root?: Element | Document | DocumentFragment): void {
    initLucideIcons({ root });
}
